import { useState, useMemo } from 'react'
import { Link as ExternalLink } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'

import QRCode from "react-qr-code"

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './download-results-to-excel.js'
import ClickToCopy from '/components/click-to-copy'

import { Box, Button, IconButton, Dialog, DialogActions, DialogContent, Tooltip } from '@mui/material'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SaveAltIcon from '@mui/icons-material/SaveAlt'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'
import { createTableStyles } from '../table-styles'
import StyledPaper from '../paper.jsx'

const ResultsGrid = ({ template, passesToProcess, makePassResults, setMakePassResults, setProcessing, progress = null }) => {

  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const [selectedRow, setSelectedRow] = useState(null)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const closeResults = () => {
    setMakePassResults([])
    setProcessing(false)
  }

  const showPassLinks = data => {
    setSelectedRow(data)
  }

  const processing = useMemo(() => makePassResults.length !== passesToProcess, [makePassResults, passesToProcess])
  const processedCount = progress?.processed ?? makePassResults.length
  const remainingCount = progress?.remaining ?? Math.max(0, passesToProcess - makePassResults.length)
  const failedCount = progress?.failed ?? makePassResults.filter(r => r.error).length

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'Pass ID',
      filterVariant: 'autocomplete',
      size: 300,
      Cell: ({ cell, row }) => row.original.error ? "ERROR" : <div className='link' onClick={() => showPassLinks(row.original)}><OpenInNewIcon className='mr-5 grey' /> {cell.getValue()}</div>,
    },
    {
      accessorKey: 'appleUrl',
      header: 'Apple Install URL',
      filterVariant: 'autocomplete',
      size: 600,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="Apple install URL copied">{cell.getValue()}</ClickToCopy>,
    },
    {
      accessorKey: 'googleUrl',
      header: 'Google Android Install URL',
      filterVariant: 'autocomplete',
      size: 600,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="Google install URL copied">{cell.getValue()}</ClickToCopy>,
    },
    {
      accessorKey: 'error',
      header: 'Error',
      filterVariant: 'autocomplete',
      size: 600,
    },
  ])

  const table = useMaterialReactTable({
    columns,
    data: makePassResults,
    state: {
      isLoading: processing
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    ...tableStyles,
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title={processing ? "Export available when processing is complete" : "Export"}>
          <span>
            <IconButton onClick={() => setDataExportDialogOpen(true)} disabled={processing}><SaveAltIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-center gap-4'>
        <div className='text-sm'>
          <b>Total:</b> {passesToProcess.toLocaleString()} &nbsp;
          <b>Processed:</b> {processedCount.toLocaleString()} &nbsp;
          <b>Left:</b> {remainingCount.toLocaleString()} &nbsp;
          <b>Failed:</b> {failedCount.toLocaleString()}
        </div>
        <Button variant='contained' onClick={closeResults}>Close</Button>
      </div>
    )

  })

  return (
    <>
      <Box sx={{ height: 'calc(100vh - 185px)', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
          <MaterialReactTable table={table} />
        </StyledPaper>
      </Box>

      {selectedRow !== null &&
        <Dialog
          open={selectedRow !== null}
          onClose={() => setSelectedRow(null)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          slotProps={{
            paper: {
              sx: {
                width: '700px',
                maxWidth: '700px',
              },
            },
          }}
        >
          <DialogContent>
            <div className='flex flex-row gap-5 justify-between'>

              <div className='flex flex-col gap-10 items-center '>
                <div><ExternalLink to={selectedRow?.appleUrl} target='_pass'><img src={appleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.appleUrl} />
                </div>
                <div>
                  <ClickToCopy source={selectedRow?.appleUrl} successMessage="Apple install URL copied">Copy URL to clipboard</ClickToCopy>
                </div>
              </div>

              <div className='flex flex-col gap-10 items-center'>
                <div><ExternalLink to={selectedRow?.googleUrl} target='_pass'><img src={googleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.googleUrl} />
                </div>
                <div>
                  <ClickToCopy source={selectedRow?.googleUrl} successMessage="Google install URL copied">Copy URL to clipboard</ClickToCopy>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex flex-row w-full p-5">
              <Button variant="outlined" onClick={() => setSelectedRow(null)}>Close</Button>
            </div>
          </DialogActions>
        </Dialog>
      }

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template.template_id}-new-passes`} data={makePassResults} excelExport={excelExport} />

    </>
  )
}

export default ResultsGrid
